import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, MapPin, BedDouble, Bath, Users, Phone, User as UserIcon, Sparkles, X, ChevronLeft, ChevronRight, Check, Wifi } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/brand";
import { loadRazorpay } from "@/lib/razorpay";

export default function ApartmentsGoaPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const handleBuyMembership = async () => {
    if (!user) {
      alert("Please login to purchase the membership.");
      return;
    }
    try {
      const Razorpay = await loadRazorpay();
      const { data } = await api.post("/memberships/create-order", { plan: "apartment_access" });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "One Light Stays",
        description: "Apartment Membership",
        order_id: data.order_id,
        prefill: {
          name: data.user.name || "",
          email: data.user.email || "",
          contact: data.user.phone || "",
        },
        theme: { color: "#1c1917" },
        handler: async (resp) => {
          try {
            await api.post("/memberships/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            window.location.reload();
          } catch (e) {
            alert(formatError(e));
          }
        },
      };
      const rzp = new Razorpay(options);
      rzp.on("payment.failed", (r) => {
        alert(r.error.description);
      });
      rzp.open();
    } catch (e) {
      alert(formatError(e));
    }
  };

  useEffect(() => {
    setLoading(true);
    api.get("/apartments")
      .then((r) => {
        setItems(r.data.apartments || []);
        setIsMember(!!r.data.is_member);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [user]);

  // Lock scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [selected]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="bg-white" data-testid="apartments-goa-page">
      <section className="relative h-[38vh] min-h-[280px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1600&q=80"
          alt="Apartments in Goa"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center text-center text-white">
          {!isMember && (
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-amber-300 mb-2">
              <Sparkles className="w-4 h-4" /> Apartment Members Only
            </div>
          )}
          {isMember && (
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-emerald-300 mb-2" data-testid="apartments-member-badge">
              <Sparkles className="w-4 h-4" /> Apartment Members Access · Unlocked
            </div>
          )}
          <h1 className="font-display text-4xl md:text-5xl">Apartments in Goa</h1>
          <p className="text-white/80 text-sm md:text-base mt-2 max-w-xl">
            Hand-picked private apartments in Goa, unlocked only for Apartment Members.
          </p>
        </div>
      </section>

      {!isMember && (
        <div className="max-w-4xl mx-auto -mt-8 px-6">
          <div className="relative z-10 rounded-2xl border border-rose-200 bg-white shadow-lg p-5 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4" data-testid="apartments-membership-banner">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display text-lg">Unlock the full list at ₹499 only</div>
                <div className="text-sm text-stone-600">
                  Become an apartment member to view apartment details & direct contact details.
                </div>
              </div>
            </div>
            <button
              onClick={handleBuyMembership}
              className="btn-primary whitespace-nowrap"
              data-testid="apartments-buy-membership"
            >
              Buy Membership
            </button>
          </div>
        </div>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-12">
        {loading ? (
          <div className="text-center text-stone-500 py-16">Loading apartments…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-stone-500 py-16" data-testid="apartments-empty">
            No apartments available at the moment. Please check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="apartments-grid">
            {items.map((a) => (
              <ApartmentCard
                key={a.id}
                a={a}
                isMember={isMember}
                onClick={() => setSelected(a)}
                onBuy={handleBuyMembership}
              />
            ))}
          </div>
        )}
      </section>

      {selected && (
        <ApartmentModal
          apartment={selected}
          isMember={isMember}
          onClose={() => setSelected(null)}
          onBuy={handleBuyMembership}
        />
      )}
    </div>
  );
}

function ApartmentCard({ a, isMember, onClick, onBuy }) {
  const cover = (a.images && a.images[0]) || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1000&q=80";
  const imageCount = (a.images && a.images.length) || 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-2xl border border-stone-200 overflow-hidden bg-white flex flex-col hover:shadow-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
      data-testid={`apartment-card-${a.id}`}
    >
      <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
        <img
          src={cover}
          alt={a.name || "Apartment"}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105`}
        />
        {a.price_per_month && (
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-full px-3 py-1 text-xs font-semibold">
            ₹{a.price_per_month} <span className="text-stone-500 font-normal">/ month</span>
          </div>
        )}
        {isMember && imageCount > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur rounded-full px-2.5 py-1 text-[0.65rem] font-semibold text-white">
            +{imageCount - 1} photos
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-xs text-stone-500 flex items-center gap-1 mb-1">
          <MapPin className="w-3.5 h-3.5" /> {a.location || "Goa"}
        </div>
        <div className={`font-display text-lg mb-2 ${!isMember ? "select-none" : ""}`}>
          {a.name || "•••••••••"}
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-stone-600 mb-3">
          {isMember && a.bedrooms > 0 && (
            <span className="inline-flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {a.bedrooms} Bed</span>
          )}
          {isMember && a.bathrooms > 0 && (
            <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5" /> {a.bathrooms} Bath</span>
          )}
          {isMember && a.max_guests > 0 && (
            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {a.max_guests} Guests</span>
          )}
        </div>
        <div className="mt-auto pt-3 border-t border-stone-100">
          <span className="text-xs font-semibold text-stone-900 group-hover:underline">
            {isMember ? (
              "View details →"
            ) : (
              <>
                🔥 Call the Owner Directly & Negotiate!
                <br />
                🚀 Unlock Membership for Just ₹499/-
              </>
            )}
          </span>
          {!isMember && (
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBuy();
              }}
              className="mt-3 btn-primary w-full py-2 text-sm text-center"
            >
              Buy Membership
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function ApartmentModal({ apartment: a, isMember, onClose, onBuy }) {
  const [index, setIndex] = useState(0);
  const images = (a.images && a.images.length > 0)
    ? a.images
    : ["https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80"];
  const count = images.length;

  const next = () => setIndex((i) => (i + 1) % count);
  const prev = () => setIndex((i) => (i - 1 + count) % count);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const handleOverlayClick = (e) => {
    if (e.target.dataset.overlay === "true") onClose();
  };

  return (
    <div
      data-overlay="true"
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ animation: "ols-fade-up 0.25s cubic-bezier(0.22,1,0.36,1) both" }}
      data-testid="apartment-modal"
    >
      <div className="relative bg-white w-full sm:max-w-3xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col shadow-2xl">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 border border-white/30 flex items-center justify-center text-white transition-all duration-200 z-50"
          data-testid="apartment-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="overflow-y-auto w-full flex-1 flex flex-col">
          {/* Image Slider */}
          <div className="relative shrink-0 bg-stone-900 aspect-[16/10] overflow-hidden">
            <img
              src={images[index]}
              alt={`${a.name || "Apartment"} ${index + 1}`}
              className={`w-full h-full object-cover`}
            />

            {isMember && count > 1 && (
              <>
                <button
                  onClick={prev}
                  aria-label="Previous image"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white flex items-center justify-center transition"
                  data-testid="apartment-modal-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={next}
                  aria-label="Next image"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white flex items-center justify-center transition"
                  data-testid="apartment-modal-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur px-3 py-1 rounded-full text-xs text-white">
                  {index + 1} / {count}
                </div>
              </>
            )}

          </div>

          {/* Thumbnails (member only) */}
          {isMember && count > 1 && (
            <div className="shrink-0 flex gap-2 px-4 py-3 border-b border-stone-100 overflow-x-auto" data-testid="apartment-modal-thumbs">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  className={`shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition ${i === index ? "border-stone-900" : "border-transparent opacity-60 hover:opacity-100"}`}
                  data-testid={`apartment-modal-thumb-${i}`}
                >
                  <img src={img} alt={`thumb ${i}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Body */}
          <div className="px-5 py-5" data-testid="apartment-modal-body">
            <div className="text-xs text-stone-500 flex items-center gap-1 mb-1">
              <MapPin className="w-3.5 h-3.5" /> {a.location || "Goa"}
            </div>
            <h2 className={`font-display text-2xl md:text-3xl mb-1 ${!isMember ? "select-none" : ""}`}>
              {isMember ? a.name : "•••••••••••••"}
            </h2>
            {a.price_per_month && (
              <div className="font-display text-xl mb-3">
                ₹{a.price_per_month} <span className="text-sm text-stone-500 font-normal">/ month</span>
              </div>
            )}

            {isMember && a.description && (
              <p className="text-stone-600 text-sm leading-relaxed mb-5">{a.description}</p>
            )}

            {isMember && (a.bedrooms > 0 || a.bathrooms > 0 || a.max_guests > 0) && (
              <div className="grid grid-cols-3 gap-3 mb-5">
                {a.bedrooms > 0 && (
                  <div className="border border-stone-200 rounded-xl p-3 text-center">
                    <BedDouble className="w-5 h-5 mx-auto text-stone-500 mb-1" />
                    <div className="font-semibold text-sm">{a.bedrooms}</div>
                    <div className="text-[0.65rem] text-stone-500 uppercase tracking-wider">Bedrooms</div>
                  </div>
                )}
                {a.bathrooms > 0 && (
                  <div className="border border-stone-200 rounded-xl p-3 text-center">
                    <Bath className="w-5 h-5 mx-auto text-stone-500 mb-1" />
                    <div className="font-semibold text-sm">{a.bathrooms}</div>
                    <div className="text-[0.65rem] text-stone-500 uppercase tracking-wider">Bathrooms</div>
                  </div>
                )}
                {a.max_guests > 0 && (
                  <div className="border border-stone-200 rounded-xl p-3 text-center">
                    <Users className="w-5 h-5 mx-auto text-stone-500 mb-1" />
                    <div className="font-semibold text-sm">{a.max_guests}</div>
                    <div className="text-[0.65rem] text-stone-500 uppercase tracking-wider">Max Guests</div>
                  </div>
                )}
              </div>
            )}

            {isMember && (a.amenities || []).length > 0 && (
              <div className="mb-5">
                <div className="font-semibold text-sm mb-2">Amenities</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {a.amenities.map((am, i) => (
                    <div key={i} className="flex items-center gap-2 text-stone-700">
                      <Check className="w-3.5 h-3.5 text-emerald-600" /> {am}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isMember && (
              <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4">
                <div className="text-[0.65rem] text-stone-500 uppercase tracking-wider mb-2">Contact Owner</div>
                <div className="flex items-center gap-2 text-sm mb-1">
                  <UserIcon className="w-4 h-4 text-stone-500" />
                  <span className="font-semibold">{a.contact_person || "—"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-stone-500" />
                  {a.phone ? (
                    <a href={`tel:${a.phone}`} className="font-semibold hover:underline">{a.phone}</a>
                  ) : "—"}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer CTA */}
        {isMember ? (
          <div className="shrink-0 px-5 py-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between gap-3">
            <p className="text-xs text-stone-500">Reach out directly to book this apartment.</p>
            {a.phone && (
              <a
                href={`tel:${a.phone}`}
                className="btn-primary text-sm whitespace-nowrap"
                data-testid="apartment-modal-call"
              >
                <Phone className="w-3.5 h-3.5" /> Call Now
              </a>
            )}
          </div>
        ) : (
          <div className="shrink-0 px-5 py-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between gap-3">
            <p className="text-xs font-semibold">
              🔥 Call the Owner Directly & Negotiate!
              <br />
              🚀 Unlock Premium Membership for Just ₹499/-
            </p>
            <button
              onClick={onBuy}
              className="btn-primary text-sm whitespace-nowrap"
              data-testid="apartment-modal-buy"
            >
              Buy Membership
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
